import { Box, Button, Card, Grid, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import sendRequest from "../dataProvider/sendRequest";
import CustomSelect from "../unit/forms/CustomSelect";
import DashboardSearchApplication from "./Search/DashboardSearchApplication";
import DashboardSearchCustomer from "./Search/DashboardSearchCustomer";
import DashboardSearchLoan from "./Search/DashboardSearchLoan";
import DashboardSearchPayment from "./Search/DashboardSearchPayment";
import DashboardSearchProduct from "./Search/DashboardSearchProduct";
import useNotify from "../unit/useNotify";
import { minWidth } from "@mui/system";

const normalizeSearchResult = (response: any): any[] => {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.results)) {
    return response.results;
  }

  if (response == null) {
    return [];
  }

  return [response];
};

type SearchFieldChangeEvent = {
  target: {
    value: string;
  };
};

const DashboardSearch = () => {
  //初始化searchType, searchField, searchKey的值分别为localStorage中dashboardSearchType, dashboardSearchField, dashboardSearchKey的值，如果localStorage中没有对应的值，则初始化为""

  const [searchType, setSearchType] = useState(
    localStorage.getItem("dashboardSearchType") || ""
  );
  const [searchField, setSearchField] = useState(
    localStorage.getItem("dashboardSearchField") || ""
  );
  const [searchFieldList, setSearchFieldList] = useState<
    { value: string; label: string }[]
  >([]);
  const [searchKey, setSearchKey] = useState(
    localStorage.getItem("dashboardSearchKey") || ""
  );
  const [searchResult, setSearchResult] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const { errorNotify } = useNotify();

  function showPlaceholder(searchType: string) {
    if (searchType === "application")
      return "Enter Application ID, Loan Num, User ID, Applicant Name";
    if (searchType === "loan") return "Enter Loan Number";
    if (searchType === "customer") return "Enter Customer ID, Name, Email";
    if (searchType === "product") return "Enter Product ID, Name";
    if (searchType === "payment") return "Enter Application ID, Loan Number";
    return "Enter search keyword";
  }

  const searchOptionsList = [
    { value: "", label: "Search category" },
    { value: "application", label: "Application" },
    { value: "loan", label: "Loan" },
    { value: "customer", label: "Customer" },
  ];

  useEffect(() => {
    if (searchType === "") {
      setSearchFieldList([{ value: "", label: "Search by" }]);
    }
    if (searchType === "application") {
      setSearchFieldList([
        { value: "", label: "Search by" },
        { value: "application_id", label: "Application ID" },
        { value: "applicant_login_email", label: "Applicant’s Login Email" },
        { value: "applicant_user_id", label: "Applicant’s User ID" },
        { value: "loan_number", label: "Loan Number" },
      ]);
    }
    if (searchType === "loan") {
      setSearchFieldList([
        { value: "", label: "Search by" },
        { value: "loan_number", label: "Loan Number" },
        { value: "application_id", label: "Application ID" },
        { value: "applicant_login_email", label: "Borrower’s Login Email" },
        { value: "borrower_user_id", label: "Borrower’s User ID" },
      ]);
    }
    if (searchType === "customer") {
      setSearchFieldList([
        { value: "", label: "Search by" },
        { value: "user_id", label: "User ID" },
        { value: "login_email", label: "Login Email" },
        { value: "mobile_phone", label: "Mobile Phone" },
        { value: "first_name", label: "First Name" },
        { value: "last_name", label: "Last Name" },
      ]);
    }
    if (searchType === "product") {
      setSearchFieldList([
        { value: "", label: "Search by" },
        { value: "product_id", label: "Product ID" },
        { value: "name", label: "Name" },
      ]);
    }
    if (searchType === "payment") {
      setSearchFieldList([
        { value: "", label: "Search by" },
        { value: "application_id", label: "Application ID" },
        { value: "loan_number", label: "Loan Number" },
      ]);
    }
  }, [searchType]);

  const handleClick = () => {
    //将searchType, searchField, searchKey存储到localStorage中，key分别为dashboardSearchType, dashboardSearchField, dashboardSearchKey
    localStorage.setItem("dashboardSearchType", searchType);
    localStorage.setItem("dashboardSearchField", searchField);
    localStorage.setItem("dashboardSearchKey", searchKey);
    if (searchType && searchKey && searchField) {
      let reqUrl = "";
      if (searchType == "application") {
        reqUrl = "quicksearch/applications";
      } else if (searchType == "loan") {
        reqUrl = "quicksearch/loans";
      } else if (searchType == "customer") {
        reqUrl = "quicksearch/customers";
      } else if (searchType == "product") {
        reqUrl = "quicksearch/products";
      } else if (searchType == "payment") {
        reqUrl = "quicksearch/payments";
      }

      if (reqUrl == "" || reqUrl == undefined) {
        return;
      }
      setLoading(true);
      sendRequest(reqUrl, {
        id: 0,
        data: {
          searchField: searchField,
          searchKey: searchKey,
        },
      }).then((response) => {
        setSearchResult(normalizeSearchResult(response));
        setLoading(false);
      });
    } else {
      errorNotify(
        "Please select search category, search by and enter search keyword"
      );
    }
  };

  //如果localStorage.getItem("dashboardSearchType")的值不为""，则自动执行handleClick函数
  useEffect(() => {
    if (
      localStorage.getItem("dashboardSearchType") &&
      localStorage.getItem("dashboardSearchType") !== "" &&
      localStorage.getItem("dashboardSearchField") &&
      localStorage.getItem("dashboardSearchField") !== "" &&
      localStorage.getItem("dashboardSearchKey") &&
      localStorage.getItem("dashboardSearchKey") !== ""
    ) {
      handleClick();
    }
  }, []);
  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: "1",
        "& a": {
          textDecoration: "none",
          color: "inherit",
        },
        borderRadius: "0px",
      }}
    >
      <Box
        sx={{
          width: "100%",
          position: "relative",
          overflow: "hidden",
          padding: "16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #e0e0e0",
          height: "75px",
          "& .icon": {
            color: "secondary.main",
          },
        }}
        id="dashboard-quicksearch-box"
      >
        <Box className="icon" sx={{ width: "150px" }}>
          <Typography
            variant="body2"
            sx={{
              ml: 0,
              fontWeight: "bold",
              fontSize: "16px",
              color: "grey",
            }}
          >
            Quick Search
          </Typography>
        </Box>
        <Grid container spacing={1} id="dashboard-quicksearch-form">
          <Grid item xs={3} sm={3}>
            <CustomSelect
              name="selecttype"
              id="dashboard-quicksearch-selecttype"
              sx={{
                width: "100%",
                minWidth: "200px",
                fontSize: "0.875rem",
                "&.MuiMenuItem-root": {
                  fontSize: "0.875rem",
                },
              }}
              variant="outlined"
              // placeholder="Select"
              value={searchType}
              onChange={(event: SearchFieldChangeEvent) => {
                setSearchType(event.target.value);
                setSearchField("");
                setSearchKey("");
                setSearchResult([]);
              }}
              optionlist={searchOptionsList}
            />
          </Grid>
          <Grid item xs={3} sm={3}>
            <CustomSelect
              name="searchField"
              id="dashboard-quicksearch-searchfield"
              sx={{
                width: "100%",
                minWidth: "200px",
                fontSize: "0.875rem",
                "&.MuiMenuItem-root": {
                  fontSize: "0.875rem",
                },
              }}
              variant="outlined"
              // placeholder="Select"
              value={searchField}
              onChange={(event: SearchFieldChangeEvent) =>
                setSearchField(event.target.value)
              }
              optionlist={searchFieldList}
            />
          </Grid>
          {/* <Grid item xs={12} sm={6} sx={{
            //两端对齐

          }}>
            <TextField
              variant="outlined"
              fullWidth
              value={searchKey}
              // placeholder={showPlaceholder(searchType)}
              placeholder="Enter search keyword"
              onChange={(e) => setSearchKey(e.target.value)}
              sx={{ width: "90%", m: 0, pt: 0 }}
            />
            <Button variant="contained" color="primary" onClick={handleClick}>
              Search
            </Button>
          </Grid> */}
          <Grid
            item
            xs={6}
            sm={6}
            sx={{
              display: "flex",
              gap: "10px",
            }}
          >
            <TextField
              id="dashboard-quicksearch-input"
              variant="outlined"
              fullWidth
              value={searchKey}
              placeholder="Enter search keyword"
              onChange={(e) => setSearchKey(e.target.value)}
              sx={{ m: 0, pt: 0 }}
            />
            <Button
              id="dashboard-quicksearch-button"
              variant="contained"
              color="primary"
              onClick={handleClick}
              sx={{ width: "120px", p: "5px" }}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Box
        sx={{
          p: 2,
          // height: "calc(100vh - 300px)",
          height: "600px",
        }}
        id="dashboard-quicksearch-result"
      >
        {loading && (
          <Typography variant="body2" sx={{ padding: "16px 0px" }}>
            Loading...
          </Typography>
        )}
        {!loading && (
          <>
            <div style={{ marginTop: "20px" }}>
              {searchType === "application" && (
                <DashboardSearchApplication searchResult={searchResult} />
              )}
              {searchType === "loan" && (
                <DashboardSearchLoan searchResult={searchResult} />
              )}
              {searchType === "customer" && (
                <DashboardSearchCustomer searchResult={searchResult} />
              )}
              {searchType === "product" && (
                <DashboardSearchProduct searchResult={searchResult} />
              )}
              {searchType === "payment" && (
                <DashboardSearchPayment searchResult={[searchResult]} />
              )}

              {searchResult == undefined || searchResult.length == 0 ? (
                <Typography
                  variant="body2"
                  sx={{
                    padding: "16px 0px",
                    color: searchKey ? "red" : "grey",
                  }}
                >
                  {searchField && searchField !== "" && `No record found`}
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ padding: "16px 0px" }}>
                  Total {searchResult.length} results found.
                </Typography>
              )}
            </div>
          </>
        )}
      </Box>
    </Card>
  );
};

export default DashboardSearch;
